import * as PIXI from 'pixi.js';
import { GameState } from '../types/GameTypes';
import { GAME_CONSTANTS } from '../utils/Constants';

export class HUD {
  private container: PIXI.Container;
  private goldText!: PIXI.Text;
  private waveText!: PIXI.Text;
  private hpBar!: PIXI.Graphics;
  private hpBarText!: PIXI.Text;
  private startWaveButton!: PIXI.Graphics;
  private startWaveText!: PIXI.Text;
  private autoButton!: PIXI.Graphics;
  private autoText!: PIXI.Text;
  private speedButton!: PIXI.Graphics;
  private speedText!: PIXI.Text;
  private timerText!: PIXI.Text;
  private killsText!: PIXI.Text;
  private damageText!: PIXI.Text;
  private restartButton!: PIXI.Graphics;
  private restartText!: PIXI.Text;
  private testGoldButton!: PIXI.Graphics;
  private testGoldText!: PIXI.Text;
  private spawnFButton!: PIXI.Graphics;
  private spawnFText!: PIXI.Text;
  private spawnGButton!: PIXI.Graphics;
  private spawnGText!: PIXI.Text;
  private spawnSButton!: PIXI.Graphics;
  private spawnSText!: PIXI.Text;
  private spawnHButton!: PIXI.Graphics;
  private spawnHText!: PIXI.Text;
  private spawnBossButton!: PIXI.Graphics;
  private spawnBossText!: PIXI.Text;
  private quickSellEnabled: boolean = true;
  private quickBuildEnabled: boolean = true;
  
  private exitButton!: PIXI.Graphics;
  private fullVersionButton!: PIXI.Graphics;
  private fullVersionButtonGlow!: PIXI.Graphics;
  private fullVersionText!: PIXI.Text;
  private fullVersionUnderscore!: PIXI.Text;
  private fullVersionPulseTime: number = 0;
  private onStartWaveCallback?: () => void;
  private onRestartCallback?: () => void;
  private onAutoCallback?: () => void;
  private onSpeedCallback?: () => void;
  private onExitCallback?: () => void;
  private onTestGoldCallback?: () => void;
  private onSpawnFCallback?: () => void;
  private onSpawnGCallback?: () => void;
  private onSpawnSCallback?: () => void;
  private onSpawnHCallback?: () => void;
  private onSpawnBossCallback?: () => void;

  constructor() {
    this.container = new PIXI.Container();
    this.setupUI();
  }

  private setupUI(): void {
    // HP Bar
    this.hpBar = new PIXI.Graphics();
    this.container.addChild(this.hpBar);

    // HP Bar Text (inside the bar)
    this.hpBarText = new PIXI.Text('100/100', {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xffffff,
      align: 'center',
    });
    // Position HUD on left side for 1080p - organized layout
    this.hpBarText.x = 30;
    this.hpBarText.y = 87;
    this.container.addChild(this.hpBarText);

    // Gold Text (combined label and value)
    this.goldText = new PIXI.Text('Gold: 0', {
      fontFamily: 'Courier New',
      fontSize: 21,
      fill: 0xffd700,
    });
    this.goldText.x = 30;
    this.goldText.y = 35;
    this.container.addChild(this.goldText);

    // Wave Text
    this.waveText = new PIXI.Text('Wave: 0', {
      fontFamily: 'Courier New',
      fontSize: 21,
      fill: 0xffffff,
    });
    this.waveText.x = 30;
    this.waveText.y = 60;
    this.container.addChild(this.waveText);

    // Start Wave Button
    this.startWaveButton = new PIXI.Graphics();
    this.startWaveButton.beginFill(0x444444);
    this.startWaveButton.drawRect(0, 0, 140, 50);
    this.startWaveButton.endFill();
    this.startWaveButton.lineStyle(2, 0x888888);
    this.startWaveButton.drawRect(0, 0, 140, 50);
    // Game control buttons - organized in left sidebar
    this.startWaveButton.x = 30;
    this.startWaveButton.y = 120; // Below HP bar
    this.startWaveButton.eventMode = 'static';
    this.startWaveButton.cursor = 'pointer';
    this.startWaveButton.on('pointerdown', () => {
      if (this.onStartWaveCallback) {
        this.onStartWaveCallback();
      }
    });
    this.container.addChild(this.startWaveButton);

    this.startWaveText = new PIXI.Text('Start Wave', {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xffffff,
    });
    this.startWaveText.x = 30 + 70 - this.startWaveText.width / 2;
    this.startWaveText.y = 120 + 25 - this.startWaveText.height / 2;
    this.container.addChild(this.startWaveText);

    // Auto Button (next to start wave button)
    this.autoButton = new PIXI.Graphics();
    this.autoButton.beginFill(0x444444);
    this.autoButton.drawRect(0, 0, 140, 50);
    this.autoButton.endFill();
    this.autoButton.lineStyle(2, 0x888888);
    this.autoButton.drawRect(0, 0, 140, 50);
    this.autoButton.x = 180; // Next to start wave button
    this.autoButton.y = 120;
    this.autoButton.eventMode = 'static';
    this.autoButton.cursor = 'pointer';
    this.autoButton.on('pointerdown', () => {
      if (this.onAutoCallback) {
        this.onAutoCallback();
      }
    });
    this.container.addChild(this.autoButton);

    this.autoText = new PIXI.Text('Auto', {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xffffff,
    });
    this.autoText.x = 180 + 70 - this.autoText.width / 2;
    this.autoText.y = 120 + 25 - this.autoText.height / 2;
    this.container.addChild(this.autoText);

    // Speed Toggle Button
    this.speedButton = new PIXI.Graphics();
    this.updateSpeedButton(1.0); // Initialize with 1x color
    this.speedButton.x = 340;
    this.speedButton.y = 120;
    this.speedButton.eventMode = 'static';
    this.speedButton.cursor = 'pointer';
    this.speedButton.on('pointerdown', () => {
      if (this.onSpeedCallback) {
        this.onSpeedCallback();
      }
    });
    this.container.addChild(this.speedButton);

    this.speedText = new PIXI.Text('1x', {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    this.speedText.x = 340 + 50 - this.speedText.width / 2;
    this.speedText.y = 120 + 25 - this.speedText.height / 2;
    this.container.addChild(this.speedText);

    // Stats at top center (timer, kills, damage) - above centered map
    this.timerText = new PIXI.Text('Time: 0:00', {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xffffff,
    });
    this.timerText.x = 920; // Center of screen (1920/2 - text width/2, approximate)
    this.timerText.y = 10;
    this.container.addChild(this.timerText);

    this.killsText = new PIXI.Text('Kills: 0', {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xffffff,
    });
    this.killsText.x = 920;
    this.killsText.y = 30;
    this.container.addChild(this.killsText);

    this.damageText = new PIXI.Text('Damage: 0', {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xffffff,
    });
    this.damageText.x = 920;
    this.damageText.y = 50;
    this.container.addChild(this.damageText);

    // Restart Button
    this.restartButton = new PIXI.Graphics();
    this.restartButton.beginFill(0x660000);
    this.restartButton.drawRect(0, 0, 120, 50);
    this.restartButton.endFill();
    this.restartButton.lineStyle(2, 0x888888);
    this.restartButton.drawRect(0, 0, 120, 50);
    // Utility buttons - below game controls
    this.restartButton.x = 30;
    this.restartButton.y = 190; // Below game control buttons
    this.restartButton.eventMode = 'static';
    this.restartButton.cursor = 'pointer';
    this.restartButton.on('pointerdown', () => {
      if (this.onRestartCallback) {
        this.onRestartCallback();
      }
    });
    this.container.addChild(this.restartButton);

    this.restartText = new PIXI.Text('Restart', {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xffffff,
    });
    this.restartText.x = 30 + 60 - this.restartText.width / 2;
    this.restartText.y = 190 + 25 - this.restartText.height / 2;
    this.container.addChild(this.restartText);

    // Test Gold Button (Purple) - Below restart button
    this.testGoldButton = new PIXI.Graphics();
    this.testGoldButton.beginFill(0x800080); // Purple
    this.testGoldButton.drawRect(0, 0, 120, 50);
    this.testGoldButton.endFill();
    this.testGoldButton.lineStyle(2, 0xaa00aa);
    this.testGoldButton.drawRect(0, 0, 120, 50);
    this.testGoldButton.x = 30;
    this.testGoldButton.y = 250; // Below restart button
    this.testGoldButton.eventMode = 'static';
    this.testGoldButton.cursor = 'pointer';
    this.testGoldButton.on('pointerdown', () => {
      if (this.onTestGoldCallback) {
        this.onTestGoldCallback();
      }
    });
    this.container.addChild(this.testGoldButton);

    this.testGoldText = new PIXI.Text('+500g', {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xffffff,
    });
    this.testGoldText.x = 30 + 60 - this.testGoldText.width / 2;
    this.testGoldText.y = 250 + 25 - this.testGoldText.height / 2;
    this.container.addChild(this.testGoldText);

    // Spawn Buttons (F, G, S, H) - 4 small squares below test gold button
    const buttonSize = 28; // Small square buttons (increased for larger text)
    const buttonSpacing = 2;
    const startX = 30;
    const startY = 310; // Below test gold button
    
    // F Button (Flying)
    this.spawnFButton = new PIXI.Graphics();
    this.spawnFButton.beginFill(0x800080); // Purple
    this.spawnFButton.drawRect(0, 0, buttonSize, buttonSize);
    this.spawnFButton.endFill();
    this.spawnFButton.lineStyle(2, 0xaa00aa);
    this.spawnFButton.drawRect(0, 0, buttonSize, buttonSize);
    this.spawnFButton.x = startX;
    this.spawnFButton.y = startY;
    this.spawnFButton.eventMode = 'static';
    this.spawnFButton.cursor = 'pointer';
    this.spawnFButton.on('pointerdown', () => {
      if (this.onSpawnFCallback) {
        this.onSpawnFCallback();
      }
    });
    this.container.addChild(this.spawnFButton);
    
    this.spawnFText = new PIXI.Text('F', {
      fontFamily: 'Courier New',
      fontSize: 21,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    this.spawnFText.x = startX + buttonSize / 2 - this.spawnFText.width / 2;
    this.spawnFText.y = startY + buttonSize / 2 - this.spawnFText.height / 2;
    this.container.addChild(this.spawnFText);
    
    // G Button (Ghost)
    this.spawnGButton = new PIXI.Graphics();
    this.spawnGButton.beginFill(0x800080);
    this.spawnGButton.drawRect(0, 0, buttonSize, buttonSize);
    this.spawnGButton.endFill();
    this.spawnGButton.lineStyle(2, 0xaa00aa);
    this.spawnGButton.drawRect(0, 0, buttonSize, buttonSize);
    this.spawnGButton.x = startX + buttonSize + buttonSpacing;
    this.spawnGButton.y = startY;
    this.spawnGButton.eventMode = 'static';
    this.spawnGButton.cursor = 'pointer';
    this.spawnGButton.on('pointerdown', () => {
      if (this.onSpawnGCallback) {
        this.onSpawnGCallback();
      }
    });
    this.container.addChild(this.spawnGButton);
    
    this.spawnGText = new PIXI.Text('G', {
      fontFamily: 'Courier New',
      fontSize: 21,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    this.spawnGText.x = startX + buttonSize + buttonSpacing + buttonSize / 2 - this.spawnGText.width / 2;
    this.spawnGText.y = startY + buttonSize / 2 - this.spawnGText.height / 2;
    this.container.addChild(this.spawnGText);
    
    // S Button (Splitter)
    this.spawnSButton = new PIXI.Graphics();
    this.spawnSButton.beginFill(0x800080);
    this.spawnSButton.drawRect(0, 0, buttonSize, buttonSize);
    this.spawnSButton.endFill();
    this.spawnSButton.lineStyle(2, 0xaa00aa);
    this.spawnSButton.drawRect(0, 0, buttonSize, buttonSize);
    this.spawnSButton.x = startX + (buttonSize + buttonSpacing) * 2;
    this.spawnSButton.y = startY;
    this.spawnSButton.eventMode = 'static';
    this.spawnSButton.cursor = 'pointer';
    this.spawnSButton.on('pointerdown', () => {
      if (this.onSpawnSCallback) {
        this.onSpawnSCallback();
      }
    });
    this.container.addChild(this.spawnSButton);
    
    this.spawnSText = new PIXI.Text('S', {
      fontFamily: 'Courier New',
      fontSize: 21,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    this.spawnSText.x = startX + (buttonSize + buttonSpacing) * 2 + buttonSize / 2 - this.spawnSText.width / 2;
    this.spawnSText.y = startY + buttonSize / 2 - this.spawnSText.height / 2;
    this.container.addChild(this.spawnSText);
    
    // H Button (Healer)
    this.spawnHButton = new PIXI.Graphics();
    this.spawnHButton.beginFill(0x800080);
    this.spawnHButton.drawRect(0, 0, buttonSize, buttonSize);
    this.spawnHButton.endFill();
    this.spawnHButton.lineStyle(2, 0xaa00aa);
    this.spawnHButton.drawRect(0, 0, buttonSize, buttonSize);
    this.spawnHButton.x = startX + (buttonSize + buttonSpacing) * 3;
    this.spawnHButton.y = startY;
    this.spawnHButton.eventMode = 'static';
    this.spawnHButton.cursor = 'pointer';
    this.spawnHButton.on('pointerdown', () => {
      if (this.onSpawnHCallback) {
        this.onSpawnHCallback();
      }
    });
    this.container.addChild(this.spawnHButton);
    
    this.spawnHText = new PIXI.Text('H', {
      fontFamily: 'Courier New',
      fontSize: 21,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    this.spawnHText.x = startX + (buttonSize + buttonSpacing) * 3 + buttonSize / 2 - this.spawnHText.width / 2;
    this.spawnHText.y = startY + buttonSize / 2 - this.spawnHText.height / 2;
    this.container.addChild(this.spawnHText);

    // Spawn Boss Button (Purple) - Below spawn mobs button
    this.spawnBossButton = new PIXI.Graphics();
    this.spawnBossButton.beginFill(0x800080); // Purple
    this.spawnBossButton.drawRect(0, 0, 120, 50);
    this.spawnBossButton.endFill();
    this.spawnBossButton.lineStyle(2, 0xaa00aa);
    this.spawnBossButton.drawRect(0, 0, 120, 50);
    this.spawnBossButton.x = 30;
    this.spawnBossButton.y = 350; // Below spawn buttons
    this.spawnBossButton.eventMode = 'static';
    this.spawnBossButton.cursor = 'pointer';
    this.spawnBossButton.on('pointerdown', () => {
      if (this.onSpawnBossCallback) {
        this.onSpawnBossCallback();
      }
    });
    this.container.addChild(this.spawnBossButton);

    this.spawnBossText = new PIXI.Text('Spawn Boss', {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xffffff,
    });
    this.spawnBossText.x = 30 + 60 - this.spawnBossText.width / 2;
    this.spawnBossText.y = 350 + 25 - this.spawnBossText.height / 2;
    this.container.addChild(this.spawnBossText);

    // Settings Button (Gear Icon) - Top right corner for 1080p
    const screenWidth = 1920;
    const screenHeight = 1080;
    const iconSize = 35;
    const iconX = screenWidth - iconSize - 10; // Top right
    const iconY = 10; // Top right
    
    this.exitButton = new PIXI.Graphics();
    
    // Draw exit/X icon
    const centerX = iconSize / 2;
    const centerY = iconSize / 2;
    const lineWidth = 3;
    const lineLength = iconSize * 0.6;
    const offset = lineLength / 2;
    
    // Draw X shape
    this.exitButton.lineStyle(lineWidth, 0xff6666, 1);
    // Top-left to bottom-right
    this.exitButton.moveTo(centerX - offset, centerY - offset);
    this.exitButton.lineTo(centerX + offset, centerY + offset);
    // Top-right to bottom-left
    this.exitButton.moveTo(centerX + offset, centerY - offset);
    this.exitButton.lineTo(centerX - offset, centerY + offset);
    
    // Draw circle background
    this.exitButton.beginFill(0x333333, 0.8);
    this.exitButton.drawCircle(centerX, centerY, iconSize / 2 - 2);
    this.exitButton.endFill();
    
    // Redraw X on top
    this.exitButton.lineStyle(lineWidth, 0xff6666, 1);
    this.exitButton.moveTo(centerX - offset, centerY - offset);
    this.exitButton.lineTo(centerX + offset, centerY + offset);
    this.exitButton.moveTo(centerX + offset, centerY - offset);
    this.exitButton.lineTo(centerX - offset, centerY + offset);
    
    this.exitButton.x = iconX;
    this.exitButton.y = iconY;
    this.exitButton.eventMode = 'static';
    this.exitButton.cursor = 'pointer';
    this.exitButton.on('pointerdown', () => {
      if (this.onExitCallback) {
        this.onExitCallback();
      }
    });
    this.exitButton.on('pointerover', () => {
      this.exitButton.clear();
      // Redraw with lighter color on hover
      this.exitButton.beginFill(0x444444, 0.9);
      this.exitButton.drawCircle(centerX, centerY, iconSize / 2 - 2);
      this.exitButton.endFill();
      this.exitButton.lineStyle(lineWidth, 0xff8888, 1);
      this.exitButton.moveTo(centerX - offset, centerY - offset);
      this.exitButton.lineTo(centerX + offset, centerY + offset);
      this.exitButton.moveTo(centerX + offset, centerY - offset);
      this.exitButton.lineTo(centerX - offset, centerY + offset);
    });
    this.exitButton.on('pointerout', () => {
      this.exitButton.clear();
      // Redraw with original color
      this.exitButton.beginFill(0x333333, 0.8);
      this.exitButton.drawCircle(centerX, centerY, iconSize / 2 - 2);
      this.exitButton.endFill();
      this.exitButton.lineStyle(lineWidth, 0xff6666, 1);
      this.exitButton.moveTo(centerX - offset, centerY - offset);
      this.exitButton.lineTo(centerX + offset, centerY + offset);
      this.exitButton.moveTo(centerX + offset, centerY - offset);
      this.exitButton.lineTo(centerX - offset, centerY + offset);
    });
    this.container.addChild(this.exitButton);

    // FULL VERSION button for itch.io builds (bottom-right corner)
    // Also show in dev mode for testing (check URL parameter ?showFullVersion=true or localhost)
    const isItchIoBuild = (typeof window !== 'undefined' && (window as any).__VITE_ITCH_IO_BUILD__ === true) ||
                          (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_ITCH_IO_BUILD === 'true');
    const isDevMode = typeof window !== 'undefined' && 
                      (window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' ||
                       new URLSearchParams(window.location.search).get('showFullVersion') === 'true');
    
    if (isItchIoBuild || isDevMode) {
      const buttonWidth = 600; // 3x bigger (200 * 3)
      const buttonHeight = 150; // 3x bigger (50 * 3)
      const buttonX = screenWidth - buttonWidth - 20; // Bottom right
      const buttonY = screenHeight - buttonHeight - 20; // Bottom right
      
      // Glow effect behind button (pulsing)
      this.fullVersionButtonGlow = new PIXI.Graphics();
      this.fullVersionButtonGlow.beginFill(0x8b5cf6, 0.5);
      this.fullVersionButtonGlow.drawRect(-15, -15, buttonWidth + 30, buttonHeight + 30); // 3x bigger glow
      this.fullVersionButtonGlow.endFill();
      this.fullVersionButtonGlow.x = buttonX;
      this.fullVersionButtonGlow.y = buttonY;
      this.container.addChild(this.fullVersionButtonGlow);
      
      // Main button
      this.fullVersionButton = new PIXI.Graphics();
      this.fullVersionButton.beginFill(0x667eea);
      this.fullVersionButton.drawRect(0, 0, buttonWidth, buttonHeight);
      this.fullVersionButton.endFill();
      this.fullVersionButton.lineStyle(6, 0x8b5cf6); // 3x bigger border (2 * 3)
      this.fullVersionButton.drawRect(0, 0, buttonWidth, buttonHeight);
      this.fullVersionButton.x = buttonX;
      this.fullVersionButton.y = buttonY;
      this.fullVersionButton.eventMode = 'static';
      this.fullVersionButton.cursor = 'pointer';
      this.fullVersionButton.on('pointerdown', () => {
        window.open('https://mazing-hunters.vercel.app/', '_blank');
      });
      this.fullVersionButton.on('pointerover', () => {
        this.fullVersionButton.clear();
        this.fullVersionButton.beginFill(0x764ba2);
        this.fullVersionButton.drawRect(0, 0, buttonWidth, buttonHeight);
        this.fullVersionButton.endFill();
        this.fullVersionButton.lineStyle(9, 0x9d6cf6); // 3x bigger border (3 * 3)
        this.fullVersionButton.drawRect(0, 0, buttonWidth, buttonHeight);
      });
      this.fullVersionButton.on('pointerout', () => {
        this.fullVersionButton.clear();
        this.fullVersionButton.beginFill(0x667eea);
        this.fullVersionButton.drawRect(0, 0, buttonWidth, buttonHeight);
        this.fullVersionButton.endFill();
        this.fullVersionButton.lineStyle(6, 0x8b5cf6); // 3x bigger border (2 * 3)
        this.fullVersionButton.drawRect(0, 0, buttonWidth, buttonHeight);
      });
      this.container.addChild(this.fullVersionButton);

      this.fullVersionText = new PIXI.Text('FULL VERSION', {
        fontFamily: 'Courier New',
        fontSize: 54, // 3x bigger (18 * 3)
        fill: 0xffffff,
        fontWeight: 'bold',
      });
      this.fullVersionText.x = buttonX + buttonWidth / 2 - this.fullVersionText.width / 2;
      this.fullVersionText.y = buttonY + buttonHeight / 2 - this.fullVersionText.height / 2 - 24; // 3x bigger offset (8 * 3)
      this.container.addChild(this.fullVersionText);

      // Pulsing underscore
      this.fullVersionUnderscore = new PIXI.Text('_', {
        fontFamily: 'Courier New',
        fontSize: 54, // 3x bigger (18 * 3)
        fill: 0xffffff,
        fontWeight: 'bold',
      });
      this.fullVersionUnderscore.x = buttonX + buttonWidth / 2 - this.fullVersionUnderscore.width / 2;
      this.fullVersionUnderscore.y = buttonY + buttonHeight / 2 - this.fullVersionUnderscore.height / 2 + 24; // 3x bigger offset (8 * 3)
      this.container.addChild(this.fullVersionUnderscore);
    }
  }

  onStartWave(callback: () => void): void {
    this.onStartWaveCallback = callback;
  }

  onAuto(callback: () => void): void {
    this.onAutoCallback = callback;
  }

  onSpeed(callback: () => void): void {
    this.onSpeedCallback = callback;
  }

  onRestart(callback: () => void): void {
    this.onRestartCallback = callback;
  }

  onExit(callback: () => void): void {
    this.onExitCallback = callback;
  }

  onTestGold(callback: () => void): void {
    this.onTestGoldCallback = callback;
  }

  onSpawnF(callback: () => void): void {
    this.onSpawnFCallback = callback;
  }
  
  onSpawnG(callback: () => void): void {
    this.onSpawnGCallback = callback;
  }
  
  onSpawnS(callback: () => void): void {
    this.onSpawnSCallback = callback;
  }
  
  onSpawnH(callback: () => void): void {
    this.onSpawnHCallback = callback;
  }

  onSpawnBoss(callback: () => void): void {
    this.onSpawnBossCallback = callback;
  }

  setAutoMode(enabled: boolean): void {
    this.autoButton.clear();
    const color = enabled ? 0x00ff00 : 0x444444;
    this.autoButton.beginFill(color);
    this.autoButton.drawRect(0, 0, 120, 40);
    this.autoButton.endFill();
    this.autoButton.lineStyle(2, enabled ? 0x00aa00 : 0x888888);
    this.autoButton.drawRect(0, 0, 120, 40);
    this.autoText.text = enabled ? 'Auto ON' : 'Auto';
    
    // Disable start wave button when auto is on
    this.startWaveButton.eventMode = enabled ? 'none' : 'static';
    this.startWaveButton.cursor = enabled ? 'default' : 'pointer';
    if (enabled) {
      this.startWaveButton.clear();
      this.startWaveButton.beginFill(0x333333);
      this.startWaveButton.drawRect(0, 0, 120, 40);
      this.startWaveButton.endFill();
      this.startWaveButton.lineStyle(2, 0x555555);
      this.startWaveButton.drawRect(0, 0, 120, 40);
    }
  }

  setSpeed(speed: number): void {
    this.speedText.text = `${speed}x`;
    this.updateSpeedButton(speed);
  }

  private updateSpeedButton(speed: number): void {
    this.speedButton.clear();
    let color: number;
    let borderColor: number;
    
    if (speed === 1.0) {
      color = 0x444444; // Grey for 1x
      borderColor = 0x888888;
    } else if (speed === 2.0) {
      color = 0x0066ff; // Blue for 2x
      borderColor = 0x0088ff;
    } else {
      color = 0xff6600; // Orange for 3x
      borderColor = 0xff8800;
    }
    
    this.speedButton.beginFill(color);
    this.speedButton.drawRect(0, 0, 100, 50);
    this.speedButton.endFill();
    this.speedButton.lineStyle(2, borderColor);
    this.speedButton.drawRect(0, 0, 100, 50);
  }

  setWaveActive(active: boolean): void {
    this.startWaveButton.clear();
    const color = active ? 0x666666 : 0x444444;
    this.startWaveButton.beginFill(color);
    this.startWaveButton.drawRect(0, 0, 120, 40);
    this.startWaveButton.endFill();
    this.startWaveButton.lineStyle(2, 0x888888);
    this.startWaveButton.drawRect(0, 0, 120, 40);
    this.startWaveText.text = active ? 'Wave Active' : 'Start Wave';
  }

  update(state: GameState, timer: number, kills: number, damage: number): void {
    // Update HP
    const hpPercent = state.hp / state.maxHp;
    const hpDisplay = `${Math.floor(state.hp)}/${state.maxHp}`;
    
    // Update HP bar
    this.hpBar.clear();
    // Background
    this.hpBar.beginFill(0x333333);
    this.hpBar.drawRect(30, 85, 200, 20);
    this.hpBar.endFill();
    // HP fill
    const hpColor = hpPercent > 0.5 ? 0x00ff00 : hpPercent > 0.25 ? 0xffff00 : 0xff0000;
    this.hpBar.beginFill(hpColor);
    this.hpBar.drawRect(30, 85, 200 * hpPercent, 20);
    this.hpBar.endFill();

    // Update HP bar text (centered in the bar)
    this.hpBarText.text = hpDisplay;
    // Center the text in the bar (200px wide, starting at x=30)
    this.hpBarText.x = 30 + (200 / 2) - (this.hpBarText.width / 2);
    this.hpBarText.y = 87; // Vertically centered in 20px bar (85 + 2px offset)

    // Update Gold
    const goldValue = Math.floor(state.economy.gold);
    this.goldText.text = `Gold: ${goldValue}`;

    // Update Wave
    const waveText = state.isSurvivalMode 
      ? `Wave: ${state.waveNumber} (Survival)`
      : `Wave: ${state.waveNumber}/${GAME_CONSTANTS.TOTAL_WAVES}`;
    this.waveText.text = waveText;

    // Update stats
    const minutes = Math.floor(timer / 60);
    const seconds = Math.floor(timer % 60);
    this.timerText.text = `Time: ${minutes}:${seconds.toString().padStart(2, '0')}`;
    this.killsText.text = `Kills: ${kills}`;
    this.damageText.text = `Damage: ${Math.floor(damage)}`;

    // Update FULL VERSION button pulsing/flashing animation
    if (this.fullVersionUnderscore) {
      // Use timer for consistent animation speed (independent of frame rate)
      this.fullVersionPulseTime = (this.fullVersionPulseTime || 0) + 0.18;
      
      // Pulse underscore opacity (flashing effect) - more dramatic
      const underscoreOpacity = Math.abs(Math.sin(this.fullVersionPulseTime));
      this.fullVersionUnderscore.alpha = underscoreOpacity;
      
      // Pulse button glow effect (behind button)
      if (this.fullVersionButtonGlow) {
        const glowAlpha = 0.4 + Math.abs(Math.sin(this.fullVersionPulseTime * 1.2)) * 0.6; // 0.4 to 1.0
        this.fullVersionButtonGlow.alpha = glowAlpha;
        
        // Pulse glow size for breathing effect
        const glowScale = 1.0 + Math.sin(this.fullVersionPulseTime * 1.5) * 0.15; // 0.85 to 1.15
        this.fullVersionButtonGlow.scale.set(glowScale, glowScale);
      }
      
      // Subtle button scale pulse (breathing effect)
      if (this.fullVersionButton) {
        const buttonScale = 1.0 + Math.sin(this.fullVersionPulseTime * 0.9) * 0.03; // 0.97 to 1.03
        this.fullVersionButton.scale.set(buttonScale, buttonScale);
      }
      
      // Pulse text slightly for extra visibility
      if (this.fullVersionText) {
        const textScale = 1.0 + Math.sin(this.fullVersionPulseTime * 0.8) * 0.04; // 0.96 to 1.04
        this.fullVersionText.scale.set(textScale, textScale);
      }
    }
  }

  getContainer(): PIXI.Container {
    return this.container;
  }

  setQuickSellEnabled(enabled: boolean): void {
    this.quickSellEnabled = enabled;
  }

  isQuickSellEnabled(): boolean {
    return this.quickSellEnabled;
  }

  setQuickBuildEnabled(enabled: boolean): void {
    this.quickBuildEnabled = enabled;
  }

  isQuickBuildEnabled(): boolean {
    return this.quickBuildEnabled;
  }
}
